import { uuid } from '../../../../utils/src';
import { PplPopoverPortalOutletFactory } from '../popover/popover-portal-outlet';
import { DomPortalOutlet, type ComponentPortal } from "@angular/cdk/portal";
import type { ApplicationRef, ComponentFactoryResolver, ComponentRef, Injector } from "@angular/core";
import type { PplPopoverPortalOutlet } from '../popover/popover-portal-outlet';


export class PplDialogPopoverPortalOutletFactory extends PplPopoverPortalOutletFactory {

  private popoverRefs: PplDialogPopoverPortalOutlet[] = [];

  create(
    outletElement: Element,
    componentFactoryResolver: ComponentFactoryResolver,
    appRef: ApplicationRef,
    defaultInjector: Injector
  ): PplPopoverPortalOutlet {
    const that = this;
    const popoverRef = new PplDialogPopoverPortalOutlet(
      outletElement,
      componentFactoryResolver,
      appRef,
      defaultInjector,
    );

    return {
      attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
        const componentRef = popoverRef.attachComponentPortal(portal);
        that.addPopoverRef(popoverRef);
        return componentRef;
      },

      detach() {
        popoverRef.detach();
        that.removePopoverRef(popoverRef.id);
      }
    };
  }

  minimizePopovers() {
    this.popoverRefs.slice().reverse().forEach(ref => {
      ref.minimize();
    });
  }

  maximizePopovers() {
    this.popoverRefs.forEach(ref => {
      ref.maximize();
    });
  }

  private addPopoverRef(popoverRef: PplDialogPopoverPortalOutlet) {
    this.popoverRefs.push(popoverRef);
  }

  private removePopoverRef(popoverRefId: string) {
    this.popoverRefs = this.popoverRefs.filter(popoverRef => popoverRef.id !== popoverRefId);
  }
}

export class PplDialogPopoverPortalOutlet implements PplPopoverPortalOutlet {

  id = uuid();

  private portalOutlet = new DomPortalOutlet(
    this.outletElement,
    this.componentFactoryResolver,
    this.appRef,
    this.defaultInjector
  );
  private componentRef: ComponentRef<any> = null;

  constructor(
    private outletElement: Element,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private defaultInjector: Injector
  ) { }

  attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    this.componentRef = this.portalOutlet.attachComponentPortal(portal);
    return this.componentRef;
  }

  detach(): void {
    this.portalOutlet.detach();
    this.componentRef = null;
  }

  minimize() {
    detachComponentRefFromAppRef(this.appRef, this.componentRef);
  }

  maximize() {
    appendComponentRefToPortalOutlet(this.portalOutlet, this.componentRef);
  }
}

export function detachComponentRefFromAppRef(appRef: ApplicationRef, componentRef: ComponentRef<any>) {
  appRef.detachView(componentRef.hostView);
  componentRef.changeDetectorRef.detectChanges();
}

export function appendComponentRefToPortalOutlet(portalOutlet: DomPortalOutlet, componentRef: ComponentRef<any>) {
  portalOutlet.outletElement.appendChild((portalOutlet as any)._getComponentRootNode(componentRef));
}
